import React from 'react'
import { fromNullable, Just, Nothing } from 'data.maybe'
import R from 'ramda'
import shallowCompare from 'react-addons-shallow-compare'
import HiddenClipboardHelper from './HiddenClipboardHelper'

import createCustomActions, * as actions from './actions'
import createSelectors from './selectors'
const selectors = createSelectors({})
const customActions = createCustomActions(selectors)

export const defaultGridTools = { ...selectors, ...actions, ...customActions }

export const createEditableGrid = gridTools => {
  const tools = gridTools || defaultGridTools
  const {
    getDataCount,
    getColCount,
    getSelectedData,
    // getSelectedBounds,
    getHeaders,
    getDataAtRow,
    //  getHeaderMap,
    isEditing,
    getEditPosition,
    getEditContent,
    getSelection,
    pasteData,
    setSelection,
    //    selectionStarts,
    selectionEnds,
    startEdit,
    cancelEdit,
    commitEdit,
    //    displayContectMenu,
    isCellEditable,
  } = tools

  const extractGridState = (store, evt) => {
    const { getState, dispatch } = store
    const state = getState()
    const selection = getSelection(state)
    const colCount = getColCount(state)
    const rowCount = getDataCount(state)
    const shift = evt.shiftKey
    const ctrl = evt.ctrlKey
    return {
      getState,
      dispatch,
      ...selection,
      colCount,
      rowCount,
      shift,
      ctrl,
    }
  }

  const arrowAction = (shift, x, y) =>
    shift
      ? selectionEnds({ rowIndex: y, columnIndex: x })
      : setSelection({ x1: x, y1: y, x2: x, y2: y })

  const left = ({ x1, y1, x2, y2, dispatch, shift }) => {
    const x = shift ? x2 - 1 : x1 - 1
    const y = shift ? y2 : y1
    if (x >= 0) dispatch(arrowAction(shift, x, y))
    return { x, y }
  }
  const right = ({ x1, y1, x2, y2, dispatch, colCount, shift }) => {
    const x = shift ? x2 + 1 : x1 + 1
    const y = shift ? y2 : y1
    if (x <= colCount - 1) dispatch(arrowAction(shift, x, y))
    return { x, y }
  }
  const up = ({ x1, y1, x2, y2, shift, dispatch }) => {
    const x = shift ? x2 : x1
    const y = shift ? y2 - 1 : y1 - 1
    if (y > 0) dispatch(arrowAction(shift, x, y))
    return { x, y }
  }

  const down = ({ x1, y1, x2, y2, shift, dispatch, rowCount }) => {
    const x = shift ? x2 : x1
    const y = shift ? y2 + 1 : y1 + 1
    if (y < rowCount) dispatch(arrowAction(shift, x, y))
    return { x, y }
  }

  const keyActions = {
    /* left */
    37: R.compose(left, extractGridState),
    /* right */
    39: R.compose(right, extractGridState),
    9: R.compose(right, extractGridState),
    38: R.compose(up, extractGridState),
    40: R.compose(down, extractGridState),
  }

  const fromEmpty = d =>
    fromNullable(d).chain(v => (R.isEmpty(v) ? Nothing() : Just(v)))

  const _nil = '$NIL$'

  const _gNil = d =>
    fromNullable(d)
      .map(d => `${d}`)
      .getOrElse('$NIL$')

  const toClipboardData = data =>
    data
      .map(
        row =>
          row.reduce((a, b) => (a === '' ? b : `${_gNil(a)}\t${_gNil(b)}`)),
        ''
      )
      .reduce((a, b) => (a === '' ? b : `${a}\r${b}`), '')

  const getCopiedData = R.compose(toClipboardData, getSelectedData)

  const copyToClipboard = ({ evt, clipboard }) => txtData => {
    clipboard.setData('Text', txtData)
    evt.preventDefault()
  }

  const clearNil = row => row.map(d => (d === _nil ? undefined : d))

  const parseClipData = rawData =>
    rawData
      .split(rawData.includes('\r') ? '\r' : '\n')
      // excel may copy extra 0 length line
      .filter(row => row.length > 0)
      .map(row => clearNil(row.split('\t')))

  const pasteToStore = ({ evt, data }) => store => {
    // const state = store.getState()
    // const bounds = getSelectedBounds(state)
    // const headers = getHeaders(state)
    // const headerMap = getHeaderMap(state)
    // bounds,headers,headerMap
    store.dispatch(pasteData({ data, isCellEditable }))
    evt.preventDefault()
  }

  const fromClipbard = clipboard =>
    fromEmpty(clipboard.getData('Text')).map(parseClipData)

  class EditableGrid extends React.Component {
    constructor(props) {
      super(props)
      this._copy = this._copy.bind(this)
      this._paste = this._paste.bind(this)
      this._keyDown = this._keyDown.bind(this)
      this.updateGrid = this.updateGrid.bind(this)
      this._onSectionRendered = this._onSectionRendered.bind(this)
      this._onMouseUp = this._onMouseUp.bind(this)

      const { store } = props

      if (store) {
        this.state = {
          colSize: getColCount(store.getState()),
          dataSize: getDataCount(store.getState()),
        }
      } else {
        this.state = {
          colSize: 0,
          dataSize: 0,
          scrollToRow: null,
          scrollToColumn: null,
        }
      }
    }

    updateGrid() {
      this.setState((state, { store }) =>
        store
          ? {
              ...state,
              colSize: getColCount(store.getState()),
              dataSize: getDataCount(store.getState()),
            }
          : { colSize: 0, dataSize: 0 }
      )
    }

    componentDidMount() {
      this.unlinkStore = fromNullable(this.props.store)
        .map(s => s.subscribe(this.updateGrid))
        .getOrElse(undefined)

      if (this.clipboardHelper && this.clipboardHelper.focus) {
        this.clipboardHelper.focus()
      }
    }

    componentWillReceiveProps(nextProps) {
      this.updateGrid(nextProps.store)
    }

    componentWillUnmount() {
      if (this.unlinkStore) this.unlinkStore()
    }

    _copy(e) {
      fromNullable(copyToClipboard)
        .ap(
          fromNullable(e.clipboardData).map(clipboard => ({
            evt: e,
            clipboard,
          }))
        )
        .ap(
          fromNullable(this.props.store)
            .map(s => s.getState())
            .map(getCopiedData)
        )
    }

    _onMouseUp(e) {
      // const { store } = this.props
      const extractPosition = evt => ({
        rowIndex: parseInt(evt.target.getAttribute('data-rowIndex')),
        columnIndex: parseInt(evt.target.getAttribute('data-columnIndex')),
      })
      const isPosition = pos => !isNaN(pos.rowIndex) && !isNaN(pos.columnIndex)
      const pos = extractPosition(e)

      if (
        this.clipboardHelper &&
        this.clipboardHelper.focus &&
        isPosition(pos)
      ) {
        e.preventDefault()
        this.clipboardHelper.focus()
      }
    }

    _keyDown(e) {
      const { store } = this.props
      const defaultAction = keyActions[e.keyCode]
      const editing = isEditing(store.getState())
      /* prevent default on enter escape and tab */
      if ([13, 9, 27].includes(e.keyCode)) {
        e.preventDefault()
      }

      if (!editing) {
        /* arrow keys */
        if (defaultAction) {
          // const {
          //   scrollToColumn: scrollToColumnPrevious,
          //   scrollToRow: scrollToRowPrevious,
          // } = this.state
          const {
            scrollToColumn,
            scrollToRow,
            scrollToRowPrevious,
          } = this.state

          // const { x: scrollToColumn, y: scrollToRow } = defaultAction(store, e)
          // const { x= scrollToColumn, y= scrollToRow } = defaultAction(store, e)

          // (scrollToRow !== scrollToRowPrevious) &&
          // (scrollToColumn !== scrollToColumnPrevious) &&
          const { fixedCol = 0 } = this.props
          const leftMost =
            this._columnStartIndex - fixedCol === 0 &&
            scrollToColumn < this._columnStartIndex + 1

          const colNotVisible =
            scrollToColumn < this._columnStartIndex + 1 ||
            scrollToColumn > this._columnStopIndex - 1
          const rowNotVisible =
            scrollToRow > this._rowStopIndex ||
            scrollToRow < this._rowStartIndex + 2
          const firstRowNoScroll =
            this._rowStartIndex === scrollToRow - 1 &&
            scrollToRow === scrollToRowPrevious &&
            scrollToColumn >= this._columnStartIndex + 1 &&
            scrollToColumn <= this._columnStopIndex - 1

          if (
            !firstRowNoScroll &&
            !leftMost &&
            (colNotVisible || rowNotVisible)
          ) {
            this.setState({
              scrollToColumn,
              scrollToRow,
              positionScrollMils: new Date().getTime(),
            })
          }
          e.preventDefault()
        } else if (e.keyCode === 46) {
          const { x1, y1, x2, y2 } = getSelection(store.getState())

          for (let rowIndex = y1; rowIndex <= y2; rowIndex++) {
            for (let columnIndex = x1; columnIndex <= x2; columnIndex++) {
              // const editInfo = { rowIndex, columnIndex }
              if (isCellEditable(rowIndex, columnIndex, store.getState())) {
                const headers = getHeaders(store.getState())
                const rowData = getDataAtRow(rowIndex, store.getState())
                const editInfo = { rowIndex, columnIndex, headers, rowData }
                store.dispatch(commitEdit(editInfo))
              }
            }
          }
          e.preventDefault()
          e.stopPropagation()
        } else if (
          !e.metaKey &&
          !e.ctrlKey &&
          ((e.keyCode >= 32 && e.keyCode <= 126) || e.keyCode === 189) &&
          e.key.length === 1
        ) {
          // try to edit
          const { x1: columnIndex, y1: rowIndex } = getSelection(
            store.getState()
          )

          store.dispatch(
            startEdit({
              columnIndex,
              rowIndex,
              content: '',
              isCellEditable,
              initKey: e.key,
            })
          )
        }

        /* editing */
      } else {
        /** escape **/
        if (e.keyCode === 27) store.dispatch(cancelEdit())
        /** enter **/
        if (e.keyCode === 13 || e.keyCode === 9) {
          const pos = getEditPosition(store.getState())
          const content = getEditContent(store.getState())
          const headers = getHeaders(store.getState())
          const rowData = getDataAtRow(pos.rowIndex, store.getState())

          const editInfo = { ...pos, content, headers, rowData }

          store.dispatch(commitEdit(editInfo))
        }
      }
    }

    _paste(e) {
      fromNullable(pasteToStore)
        .ap(
          fromNullable(e.clipboardData).chain(clip =>
            fromClipbard(clip).map(data => ({ evt: e, data }))
          )
        )
        .ap(fromNullable(this.props.store))
    }

    shouldComponentUpdate(nextProps, nextState) {
      return shallowCompare(this, nextProps, nextState)
    }

    _onSectionRendered({
      columnStartIndex,
      columnStopIndex,
      rowStartIndex,
      rowStopIndex,
    }) {
      const { fixedCol = 0 } = this.props

      this._columnStartIndex = columnStartIndex + fixedCol
      this._columnStopIndex = columnStopIndex + fixedCol
      this._rowStartIndex = rowStartIndex
      this._rowStopIndex = rowStopIndex
    }

    render() {
      const {
        colSize,
        dataSize,
        scrollToRow,
        scrollToColumn,
        positionScrollMils,
      } = this.state
      return this.props.children({
        colSize,
        dataSize,
        scrollToRow,
        scrollToColumn,
        positionScrollMils,
        onSectionRendered: this._onSectionRendered,
        onCopy: this._copy,
        onPaste: this._paste,
        onKeyDown: this._keyDown,
        onContextMenu: this._contextMenu,
        // onClick: this._onClick,
        onMouseUp: this._onMouseUp,
        hiddenClipboardHelper: (
          <HiddenClipboardHelper
            innerRef={r => (this.clipboardHelper = r)}
            type="text"
          />
        ),
      })
    }
  }
  return EditableGrid
}

export default createEditableGrid()
